import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
} from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import moment from "moment";
import { UIService } from "@bca-core/services/ui.service";
import { Moment } from "moment";
import { TranslateService } from "@ngx-translate/core";
import { AvoService } from "@bca-shared/utils/avo/avo.service";

@Component({
  selector: "bca-choose-birth-date",
  templateUrl: "choose-birth-date.component.html",
  styleUrls: ["choose-birth-date.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChooseBirthDateComponent implements OnInit, OnDestroy {
  formDate: FormGroup;
  birthDateSelected: string;
  private onDestroy = new Subject<undefined>();

  private lastReportedBirthDateError: string | undefined;

  @Input()
  set selectedDate(date: string) {
    this.birthDateSelected = date;
  }
  @Input()
  dateRequiredError: boolean;
  @Input()
  dateValidError: boolean;
  @Input()
  dateAgeError: boolean;

  @Output()
  dateSelected = new EventEmitter<Date>();
  @Output()
  dateStringSelected = new EventEmitter<string>();

  constructor(
    private _uiService: UIService,
    private _translate: TranslateService,
    private _avoService: AvoService
  ) {}

  ngOnInit(): void {
    this.formDate = new FormGroup({
      birthDate: new FormControl(null, [
        Validators.required,
        Validators.minLength(10),
        this.checkAge.bind(this),
      ]),
    });

    if (this.birthDateSelected) {
      this.formDate.setValue({ birthDate: this.birthDateSelected });
    }
    // if (this._uiService.userForm) {
    // this.subscriptions = this._uiService.userForm.pipe(takeUntil(this.onDestroy)).subscribe(val => {
    this.formDate.statusChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe((status: string) => {
        const inputValue = status === "VALID" ? this.birthDate.value : null;
        this.selectDate(inputValue);
      });

    if (!this._uiService.watchStorage) {
      return;
    }
    this._uiService
      .watchStorage()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((data: any) => {
        const product = data;
        const isWithSavedUser = product && product.user;
        const isWithSavedUserAndNoDate =
          isWithSavedUser &&
          (product.user.birthDate === null ||
            !product.user.birthDate.dateString);
        const isWithSavedUserAndDate =
          isWithSavedUser &&
          !isWithSavedUserAndNoDate &&
          product.user.birthDate != null &&
          product.user.birthDate.dateString != null;
        const isWithSavedUserAndDateString =
          isWithSavedUserAndDate &&
          product.user.birthDate.dateString &&
          product.user.birthDate.dateString.trim().length === 10;
        let isEmptyDate = true;
        if (isWithSavedUser) {
          isEmptyDate = isWithSavedUserAndNoDate;
        }
        if (isWithSavedUserAndDateString) {
          this.formDate.setValue({
            birthDate: product.user.birthDate.dateString,
          });
        }
        const isResetFormDate = isEmptyDate && this.birthDate.value;

        if (isResetFormDate || isWithSavedUserAndNoDate) {
          this.formDate.reset();
        }
      });
  }
  get birthDate(): AbstractControl {
    return this.formDate.get("birthDate");
  }

  getCombinedError(): string | undefined {
    const formTagId = "birthDate";
    let errorMessage: string | undefined;
    if (
      this.dateValidError &&
      this.birthDate.errors &&
      this.birthDate.errors.required
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.BIRTH_DATE.ERROR_REQUIRED",
      );
    } else if (
      this.dateValidError &&
      this.birthDate.errors &&
      this.birthDate.errors.birthDateNotValid
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.BIRTH_DATE.ERROR_NO_VALID",
      );
    } else if (
      !this.dateValidError &&
      this.dateAgeError &&
      this.birthDate.errors &&
      this.birthDate.errors.birthDateNotValid
    ) {
      errorMessage = this._translate.instant(
        "USER_DATA.INPUT.BIRTH_DATE.ERROR_MINMALE_AGE",
      );
    } else {
      errorMessage = undefined;
    }

    if (errorMessage && errorMessage !== this.lastReportedBirthDateError) {
      this._avoService.formError({
        formName: "user_data_name",
        formId1: formTagId,
        errorMessage: [errorMessage],
      });
      this.lastReportedBirthDateError = errorMessage;
    }

    if (!errorMessage) {
      this.lastReportedBirthDateError = undefined;
    }

    return errorMessage;
  }

  isNumberOnly(evt): boolean {
    const charCode = evt.which ? evt.which : evt.keyCode;
    if (charCode !== 45 && charCode > 31 && (charCode < 48 || charCode > 57))
      return false;

    return true;
  }

  doCheckAgeOnChange(input: string): void {
    const dateState = new DateState(input);
    const val = dateState.val;
    dateState.days =
      val.substring(0, 2).match(/\d/g) &&
      val.substring(0, 2).match(/\d/g).length === 2
        ? val.substring(0, 2).match(/\d/g).join("")
        : null;
    dateState.months =
      val.substring(3, 5).match(/\d/g) &&
      val.substring(3, 5).match(/\d/g).length === 2
        ? val.substring(3, 5).match(/\d/g).join("")
        : null;
    dateState.years =
      val.substring(6, 10).match(/\d/g) &&
      val.substring(6, 10).match(/\d/g).length === 4
        ? val.substring(6, 10).match(/\d/g).join("")
        : null;
    dateState.today = moment(new Date());
    if (dateState.isDayError()) {
      this.dateValidError = true;
    } else if (dateState.isMonthError()) {
      this.dateValidError = true;
    } else if (dateState.isYearError()) {
      this.dateValidError = true;
    } else if (!dateState.isDateFormatOk()) {
      this.dateValidError = true;
    } else if (dateState.isDateFormatOk() && dateState.isYearOk()) {
      const date = dateState.initializeInputDate();
      const limit = moment(
        dateState.today.date() +
          "-" +
          (dateState.today.month() + 1) +
          "-" +
          (dateState.today.year() - 12),
        "DD-MM-YYYY",
      );
      const setMin = dateState.today.diff(limit, "months");
      const checkMin = dateState.today.diff(date, "months");
      const checkDate = date.utc().isValid() && dateState.inputDate.isValid();
      if (!checkDate) {
        this.dateValidError = true;
      } else if (checkMin < setMin) {
        this.dateAgeError = true;
        this.dateValidError = false;
      } else {
        this.dateAgeError = false;
        this.dateValidError = false;
      }
    } else {
      this.dateAgeError = false;
      this.dateValidError = true;
    }
  }
  /**
   * if age is correct then return null, otherwise return validation error.
   * @param control
   */
  checkAge(control: AbstractControl): { [key: string]: boolean } | null {
    let valid = { birthDateNotValid: true };
    const dateState = new DateState(control.value);
    if (!dateState.val) {
      return valid;
    }
    this.doCheckAgeOnChange(dateState.val);
    if (!this.dateValidError && !this.dateAgeError) {
      valid = null;
    }

    return valid;
  }

  onKeyup($event: any): void {
    const day = this.padWithZero($event.detail.values.day);
    const month = this.padWithZero($event.detail.values.month);
    const year = $event.detail.values.year;

    const formattedDate = day + "-" + month + "-" + year;
    this.formDate.get("birthDate")!.setValue(formattedDate);
  }

  private padWithZero(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }

  ngOnDestroy(): void {
    this.onDestroy.next(undefined);
  }

  selectDate(value: string): void {
    this.dateStringSelected.emit(value);
    if (value) {
      const input = new Date(moment(value, "DD-MM-YYYY").format());
      this.dateSelected.emit(input);
    } else {
      this.dateSelected.emit(null);
    }
  }
}
export class DateState {
  val: string;
  days: string;
  months: string;
  years: string;
  today: Moment;
  inputDate: Moment;
  constructor(input: string) {
    this.val = input ? input : "";
  }
  initializeInputDate(): any {
    this.inputDate = moment(this.val, "DD-MM-YYYY");
    return this.inputDate;
  }

  isDayError(): boolean {
    return (
      this.val &&
      this.val.length >= 2 &&
      (!this.days || parseInt(this.days) > 31)
    );
  }
  isMonthError(): boolean {
    return (
      this.val &&
      this.val.length >= 4 &&
      (!this.months || parseInt(this.months) > 12)
    );
  }
  isYearError(): boolean {
    return (
      this.val &&
      this.val.length >= 7 &&
      (!this.years ||
        parseInt(this.years) > this.today.year() ||
        parseInt(this.years) < this.today.year() - 99)
    );
  }
  isDayOk(): boolean {
    if (!this.val) {
      return false;
    }
    const day = this.val.split("-")[0];
    return (
      day &&
      day.length === 2 &&
      this.isPositiveNumber(day) &&
      parseInt(day) <= 31
    );
  }
  isMonthOk(): boolean {
    if (!this.isDayOk()) {
      return false;
    }
    const month = this.val.split("-")[1];
    return (
      month &&
      month.length === 2 &&
      this.isPositiveNumber(month) &&
      parseInt(month) <= 12
    );
  }
  isYearOk(): boolean {
    if (!this.isMonthOk()) {
      return false;
    }
    const year = this.val.split("-")[2];
    return year && year.length === 4 && this.isPositiveNumber(year);
  }
  isDayOkAndOneDigit(): boolean {
    const month = this.val.split("-")[0];
    return (
      month &&
      month.length === 1 &&
      this.isPositiveNumber(month) &&
      this.val.split("-").length === 3
    );
  }
  isMonthOkAndOneDigit(): boolean {
    const day = this.val.split("-")[1];
    return (
      day &&
      day.length === 1 &&
      this.isPositiveNumber(day) &&
      this.val.split("-").length === 3
    );
  }
  isDateFormatOk(): boolean {
    if (this.val.charAt(2) && this.val.charAt(2) !== "-") {
      return false;
    }
    if (this.val.charAt(5) && this.val.charAt(5) !== "-") {
      return false;
    }
    if (this.val.indexOf("--") >= 0) {
      return false;
    }
    return this.val && this.val.length === 10;
  }
  isPositiveNumber(value: string): boolean {
    if (value === null) {
      return false;
    }
    return !isNaN(Number(value.toString())) && Number(value.toString()) > 0;
  }
}
